'use client';

import SettingConfig from '@module/config/setting';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { Popover, PopoverContent, PopoverTrigger } from '@pc/components/ui/popover';
import type { MouseEvent } from 'react';
import { useEffect, useState } from 'react';
const COOKIE_NAME = 'sessKey';
const PARAM_NAME = 'sessKey';
const SESSKEY_RE = /^[A-Za-z0-9._-]{8,512}$/;
const WINDOW_TARGET = '_blank';
const WINDOW_FEATURES = 'noopener,noreferrer';
const isProd = SettingConfig.isProd || SettingConfig.ENVIRONMENT === 'production-local-pandatv';
const FANZA_DOMAIN = isProd ? 'pandalive.co.jp' : 'dev.pandalive.co.jp';
const readCookie = (name: string) => {
  const raw = document.cookie ?? '';
  if (!raw) return null;
  for (const entry of raw.split('; ')) {
    const [key, ...rest] = entry.split('=');
    if (key !== name) continue;
    const value = rest.join('=');
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  return null;
};
export default function FanzaEntryLink() {
  const {
    loginInfo
  } = useGetLoginInfoQuery();
  const [initial, setInitial] = useState('');
  const [sessKey, setSessKey] = useState('');
  const [port, setPort] = useState('');
  useEffect(() => {
    setSessKey((readCookie(COOKIE_NAME) ?? '').trim());
  }, [loginInfo?.loginInfo.sessKey]);
  if (!loginInfo?.loginInfo.console) return null;
  const trimmedInitial = (initial ?? '').trim();
  const trimmedPort = (port ?? '').trim();
  const trimmedSessKey = (sessKey ?? '').trim();
  const bktvHref = (() => {
    if (!trimmedSessKey) return '';
    const host = trimmedInitial ? `${trimmedInitial}.${FANZA_DOMAIN}` : trimmedPort ? `local.${FANZA_DOMAIN}` : FANZA_DOMAIN;
    try {
      const url = new URL(`https://${host}${trimmedPort ? `:${trimmedPort}` : ''}/`);
      url.searchParams.set(PARAM_NAME, trimmedSessKey);
      return url.toString();
    } catch {
      return '';
    }
  })();
  const handleOpenBktv = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (!trimmedSessKey) {
      alert(`[sessKey] Missing cookie: "${COOKIE_NAME}"`);
      return;
    }
    if (!SESSKEY_RE.test(trimmedSessKey)) {
      alert(`[sessKey] Invalid cookie value for "${COOKIE_NAME}": ${trimmedSessKey}`);
      return;
    }
    try {
      const host_0 = trimmedInitial ? `${trimmedInitial}.${FANZA_DOMAIN}` : trimmedPort ? `local.${FANZA_DOMAIN}` : FANZA_DOMAIN;
      const url_0 = new URL(`https://${host_0}${trimmedPort ? `:${trimmedPort}` : ''}/`);
      url_0.searchParams.set(PARAM_NAME, trimmedSessKey);
      const opened = window.open(url_0.toString(), WINDOW_TARGET, WINDOW_FEATURES);
      void opened;
    } catch (error) {
      alert(`[sessKey] Failed to open BKTV link${error instanceof Error ? `: ${error.message}` : '.'}`);
    }
  };
  return <Popover>
      <PopoverTrigger className='mr-2 flex h-10 items-center gap-1 rounded-full px-3 font-semibold text-[13px] text-purple-600 ring-1 ring-purple-300 hover:bg-menuHover focus:outline-none'>
        <svg className='size-3.5 shrink-0' viewBox='0 0 24 24' fill='currentColor' aria-hidden>
          <title>판피입장</title>
          <path d='M12 2l2.09 6.26L20.5 9.27l-5 3.64L16.82 20 12 16.9 7.18 20l1.32-7.09-5-3.64 6.41-.99L12 2z' />
        </svg>
        판피
        <span className='rounded bg-purple-100 px-1 py-0.5 font-bold text-[10px] text-purple-500 uppercase'>
          {isProd ? 'prd' : 'dev'}
        </span>
        <svg className='size-3.5 shrink-0' viewBox='0 0 24 24' fill='currentColor' aria-hidden>
          <title>판피입장</title>
          <path d='M12 2l2.09 6.26L20.5 9.27l-5 3.64L16.82 20 12 16.9 7.18 20l1.32-7.09-5-3.64 6.41-.99L12 2z' />
        </svg>
      </PopoverTrigger>
      <PopoverContent align='end' sideOffset={8} className='flex w-72 flex-col gap-4 p-4'>
        <span className='font-semibold text-slate-500 text-xs uppercase tracking-wide'>판피입장권</span>
        <label className='flex flex-col gap-1 text-sm'>
          <span className='font-semibold text-slate-800'>Subdomain - Initial (optional)</span>
          <input type='text' value={initial} onChange={e => setInitial(e.target.value)} placeholder='예: cjh' className='h-9 rounded-lg border border-slate-200 bg-white px-3 text-slate-900 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200' />
        </label>
        <label className='flex flex-col gap-1 text-sm'>
          <span className='font-semibold text-slate-800'>Port (optional)</span>
          <input type='text' value={port} onChange={e_0 => setPort(e_0.target.value)} placeholder='예: 3032' className='h-9 rounded-lg border border-slate-200 bg-white px-3 text-slate-900 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200' />
        </label>
        <a href={bktvHref || '#'} target='_blank' rel='noopener noreferrer' onClick={handleOpenBktv} className='flex min-h-[40px] items-center justify-center overflow-hidden rounded-xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 px-3 py-2 font-semibold text-sm text-white shadow-lg transition-all hover:scale-[1.01] hover:shadow-xl active:scale-[0.99]'>
          <span className='truncate'>{bktvHref || `https://${FANZA_DOMAIN}[:port]/?sessKey={cookie}`}</span>
        </a>
      </PopoverContent>
    </Popover>;
}